import * as valibot from 'valibot';
import type { Endpoint } from './endpoint';
import type { StorageLike } from './storage';

/** LocalStorage key for the cached route payload. */
export const LAST_ROUTE_KEY = 'router.lastRoute.v1';

/** Coordinate rounding precision for position-endpoint signatures. */
const LAST_ROUTE_COORD_DECIMALS = 5;

/**
 * BCP-47 tag driving `toLocaleLowerCase` in {@link addressSignature}. German
 * because the frontend targets Berlin addresses (`ß`/umlauts must fold the
 * same way when the browser locale is arbitrary — otherwise `Müllerstr` and
 * `MÜLLERSTR` compare unequal on locales that don't own the mapping).
 */
const ADDRESS_LOCALE = 'de';

/** Signature for the `{ kind: 'address' }` endpoint variant. */
function addressSignature(endpoint: Extract<Endpoint, { kind: 'address' }>): string {
  const normalized = endpoint.address.normalize('NFC').trim().toLocaleLowerCase(ADDRESS_LOCALE);
  return normalized ? `address:${normalized}` : '';
}

/** Signature for the `{ kind: 'position' }` endpoint variant. */
function positionSignature(endpoint: Extract<Endpoint, { kind: 'position' }>): string {
  if (!Number.isFinite(endpoint.latitude) || !Number.isFinite(endpoint.longitude)) return '';
  const latitude = endpoint.latitude.toFixed(LAST_ROUTE_COORD_DECIMALS);
  const longitude = endpoint.longitude.toFixed(LAST_ROUTE_COORD_DECIMALS);
  return `position:${latitude},${longitude}`;
}

/**
 * Stable string identifying one endpoint. Returns '' for undefined, a blank
 * address, or a position with non-finite coords; callers use empty-string as
 * "half-formed, drop me". Stop signatures are trusted — a NaN `stopId` yields
 * `stop:NaN`.
 */
export function endpointSignature(endpoint: Endpoint | undefined): string {
  if (!endpoint) return '';
  if (endpoint.kind === 'stop') return `stop:${String(endpoint.stopId)}`;
  if (endpoint.kind === 'address') return addressSignature(endpoint);
  return positionSignature(endpoint);
}

/**
 * Composes both endpoint signatures plus the block-params suffix so a route
 * computed without excluded lines/modes never restores as a route with them
 * (or vice versa). Returns undefined when either endpoint side is empty.
 */
export function composeRouteSignature(
  fromSig: string,
  toSig: string,
  blockSuffix: string,
): string | undefined {
  if (!fromSig || !toSig) return undefined;
  return `${fromSig}|${toSig}|${blockSuffix}`;
}

/**
 * Writes `{signature, response}` under {@link LAST_ROUTE_KEY}. No-op when
 * `signature` is undefined/empty or `response` is `undefined`. Storage failures
 * (quota / private mode) are silently swallowed. Pair with
 * {@link loadCachedRouteFor} and {@link purgeLastRoute}.
 */
export function saveLastRoute(
  storage: StorageLike,
  signature: string | undefined,
  response: unknown,
): void {
  if (!signature || response === undefined) return;
  try {
    storage.setItem(LAST_ROUTE_KEY, JSON.stringify({ signature, response }));
  } catch {
    /* quota / private mode */
  }
}

/**
 * Removes the cached route; ignores storage errors + missing `removeItem`.
 * Does not clear the module-scoped parse memo (a subsequent null storage read
 * short-circuits before it is consulted).
 */
export function purgeLastRoute(storage: StorageLike): void {
  try {
    storage.removeItem?.(LAST_ROUTE_KEY);
  } catch {
    /* best effort */
  }
}

/** Runtime schema for the persisted route cache payload; both fields are opaque payloads. */
const CachedEntrySchema = valibot.object({
  signature: valibot.optional(valibot.unknown()),
  response: valibot.optional(valibot.unknown()),
});

/** Shape of the persisted route cache payload, inferred from {@link CachedEntrySchema}. */
type CachedEntry = valibot.InferOutput<typeof CachedEntrySchema>;

/** Memoized parse output keyed by the raw string it was parsed from. */
interface ParseCache {
  rawText: string;
  entry: CachedEntry | undefined;
}

/**
 * Cache of the last parsed payload keyed by (storage identity, raw string). A
 * ~10-50 kB route response is expensive to re-parse; callers that check the
 * cache twice (initial mount + effect) skip the second parse when the string
 * is unchanged. Keyed by storage object identity so tests that inject a
 * distinct memory storage stay isolated from each other.
 */
const parseStateByStorage = new WeakMap<StorageLike, ParseCache>();

/** Return the response iff the entry matches `signature`. */
function matchingResponse(entry: CachedEntry | undefined, signature: string): unknown {
  if (!entry || entry.signature !== signature || !entry.response) return undefined;
  return entry.response;
}

/** Narrow an arbitrary parsed value to the {@link CachedEntry} shape, or undefined. */
function toCachedEntry(value: unknown): CachedEntry | undefined {
  const result = valibot.safeParse(CachedEntrySchema, value);
  return result.success ? result.output : undefined;
}

/** Parse and memoize `rawJson`; on syntax error, wipe the cache and purge storage. */
function parseAndMemoize(storage: StorageLike, rawJson: string): CachedEntry | undefined {
  try {
    const parsed: unknown = JSON.parse(rawJson);
    const entry = toCachedEntry(parsed);
    parseStateByStorage.set(storage, { rawText: rawJson, entry });
    return entry;
  } catch {
    parseStateByStorage.delete(storage);
    purgeLastRoute(storage);
    return undefined;
  }
}

/** Return the memoized parse iff its raw string matches `rawJson`. */
function memoizedParse(storage: StorageLike, rawJson: string): CachedEntry | undefined {
  const cache = parseStateByStorage.get(storage);
  return cache?.rawText === rawJson ? cache.entry : undefined;
}

/**
 * Reads the cached response for `signature`, or undefined on miss, mismatch,
 * or undefined signature. Memoizes the parse across calls; purges a corrupted
 * payload so a bricked cache does not re-parse every reload.
 */
export function loadCachedRouteFor(storage: StorageLike, signature: string | undefined): unknown {
  if (!signature) return undefined;
  const rawJson = storage.getItem(LAST_ROUTE_KEY);
  if (!rawJson) return undefined;
  const entry = memoizedParse(storage, rawJson) ?? parseAndMemoize(storage, rawJson);
  return matchingResponse(entry, signature);
}

export { type StorageLike } from './storage';
